import { FC, useEffect, useState } from 'react';
import FSMSubscriptionModal from './components/FSMSubscriptionModal';
import { selectCurrentTeam, selectIsOwner, selectMerchantCustomizations } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FSMPlan } from 'containers/settings/constants';
import FSMNotAvaialbleModal from './components/FSMNotAvaialbleModal';
import FSMPlanComparisonModal from './components/FSMPlanComparisonModal';
import { usePostSubscriptionActivityMutation } from 'containers/app/api';
import { FSM_PLANS } from './constants';
import UpgradeFSMModal from './components/UpgradeFSMModal';
import SubscribedModal from './components/SubscribedModal';
import routesPath from 'routes/RoutesPath';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldShowFSMModal } from './utils';
import { FSMSubscriptionIcon } from 'assets/icons';
import { resetCreateJobState } from 'containers/jobs/create-edit-job/createJobSlice';

interface GlobalModalRendererProps {
  isFullScreenLayout: boolean;
}

const GlobalModalRenderer: FC<GlobalModalRendererProps> = ({ isFullScreenLayout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { subscriptionActivity } = useAppSelector(selectMerchantCustomizations);
  const { currentFSMPlan } = subscriptionActivity ?? {};
  const isOwner = useAppSelector(selectIsOwner);
  const { id: teamId } = useAppSelector(selectCurrentTeam);
  const dispatch = useAppDispatch();
  const showSubscriptionModal = shouldShowFSMModal(location.pathname) ?? false;

  const [postSubscriptionActivity, { isLoading: isPostSubscriptionActivityLoading }] =
    usePostSubscriptionActivityMutation();

  const [showModal, setShowModal] = useState(true);
  const [showFSMComparePlansModal, setShowFSMComparePlansModal] = useState(false);
  const [showSubscribedModal, setShowSubscribedModal] = useState(false);

  useEffect(() => {
    setShowModal(true);
  }, [currentFSMPlan]);

  const handleChoosePlanClick = () => {
    setShowFSMComparePlansModal(true);
  };

  const handleCloseFSMComparePlansModal = () => {
    setShowFSMComparePlansModal(false);
  };

  const handleChoosePlan = async (plan: string) => {
    await postSubscriptionActivity({
      teamId,
      payload: {
        feature: 'FSM',
        webFreePlanSelected: plan === FSM_PLANS.FIELD_SERVE,
        webPaidPlanSelected: plan === FSM_PLANS.FIELD_SERVE_PLUS
      }
    });
    handleCloseFSMComparePlansModal();
    if (currentFSMPlan === FSMPlan.NO_PLAN) {
      setShowModal(false);
      setShowSubscribedModal(true);
    }
  };

  const handleCreateJobClick = () => {
    dispatch(resetCreateJobState());
    navigate(routesPath.CREATE_JOB);
    handleCloseSubscribedModal();
  };

  const handleCloseSubscribedModal = () => {
    setShowSubscribedModal(false);
  };

  const goToPreviousPage = () => {
    navigate(-1);
  };

  if (showSubscriptionModal || showSubscribedModal) {
    if (isOwner) {
      return (
        <>
          {currentFSMPlan === FSMPlan.NO_PLAN && showModal && (
            <FSMSubscriptionModal
              onChoosePlan={handleChoosePlanClick}
              showCloseButton={isFullScreenLayout}
              handleClose={goToPreviousPage}
            />
          )}
          {(currentFSMPlan === FSMPlan.FREE || currentFSMPlan === FSMPlan.CANCELLED) && showModal && (
            <UpgradeFSMModal
              onComparePlans={handleChoosePlanClick}
              onStartTrial={() => handleChoosePlan(FSM_PLANS.FIELD_SERVE_PLUS)}
              isLoading={isPostSubscriptionActivityLoading}
            />
          )}
          {showFSMComparePlansModal && showModal && (
            <FSMPlanComparisonModal
              handleClose={handleCloseFSMComparePlansModal}
              onChoosePlan={handleChoosePlan}
              isLoading={isPostSubscriptionActivityLoading}
              currentFSMPlan={currentFSMPlan}
            />
          )}
          {showSubscribedModal && (
            <SubscribedModal
              title="You're all set!"
              description={
                <div className="text-sbase font-medium text-primaryText">
                  Field Serve is now active for your business.
                  <div className="mt-5"> Let's create your first job.</div>
                </div>
              }
              primaryButtonText="Create new job"
              secondaryButtonText="Done"
              primaryButtonClick={handleCreateJobClick}
              onClose={handleCloseSubscribedModal}
              icon={<FSMSubscriptionIcon />}
            />
          )}
        </>
      );
    }
    return <FSMNotAvaialbleModal showCloseButton={isFullScreenLayout} handleClose={goToPreviousPage} />;
  }
  return null;
};

export default GlobalModalRenderer;
